import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  createMicrophoneInput,
  type MicrophoneInput,
} from "@/lib/microphone-input";

/** Record once, then return the transcript for editing or explicit immediate sending. */
export function useDictation(onText: (text: string) => void) {
  const [recording, setRecording] = useState(false);
  const [starting, setStarting] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [microphoneLabel, setMicrophoneLabel] = useState("");
  const recorderRef = useRef<MediaRecorder | null>(null);
  const inputRef = useRef<MicrophoneInput | null>(null);
  const lockedRef = useRef(false);
  const generationRef = useRef(0);
  const completeRef = useRef<((text: string) => void) | undefined>(undefined);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;

  useEffect(
    () => () => {
      ++generationRef.current;
      const recorder = recorderRef.current;
      if (recorder) {
        recorder.onstop = null;
        if (recorder.state !== "inactive") recorder.stop();
      }
      inputRef.current?.stop();
      inputRef.current = null;
      recorderRef.current = null;
      lockedRef.current = false;
    },
    [],
  );

  const start = useCallback(async () => {
    if (lockedRef.current) return;
    lockedRef.current = true;
    const generation = ++generationRef.current;
    const current = () => generation === generationRef.current;
    setStarting(true);
    try {
      const input = await createMicrophoneInput();
      if (!current()) {
        input.stop();
        return;
      }
      inputRef.current = input;
      setMicrophoneLabel(input.label);
      const mime = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "";
      const recorder = new MediaRecorder(
        input.stream,
        mime ? { mimeType: mime } : undefined,
      );
      const chunks: Blob[] = [];
      completeRef.current = undefined;
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };
      recorder.onstop = async () => {
        input.stop();
        inputRef.current = null;
        recorderRef.current = null;
        if (!current()) return;
        setRecording(false);
        setTranscribing(true);
        try {
          const blob = new Blob(chunks, {
            type: recorder.mimeType || "audio/webm",
          });
          if (!blob.size) {
            toast.error("Keine Aufnahme erkannt. Bitte erneut versuchen.");
            return;
          }
          const form = new FormData();
          const ext = (recorder.mimeType || "audio/webm").includes("mp4")
            ? "mp4"
            : "webm";
          form.append("audio", blob, `recording.${ext}`);
          const res = await fetch("/api/ai/transcribe", {
            method: "POST",
            credentials: "same-origin",
            body: form,
          });
          if (!res.ok) throw new Error("Transcription failed");
          const { text } = (await res.json()) as { text: string };
          if (!current()) return;
          if (text?.trim())
            (completeRef.current ?? onTextRef.current)(text.trim());
          else toast.error("Keine Sprache erkannt. Bitte erneut versuchen.");
        } catch {
          if (current())
            toast.error(
              "Transkription fehlgeschlagen. Bitte erneut versuchen.",
            );
        } finally {
          if (current()) {
            lockedRef.current = false;
            setTranscribing(false);
            setMicrophoneLabel("");
          }
        }
      };
      recorderRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch {
      if (current()) {
        inputRef.current?.stop();
        inputRef.current = null;
        recorderRef.current = null;
        lockedRef.current = false;
        toast.error("Kein Mikrofonzugriff. Bitte Berechtigung erlauben.");
      }
    } finally {
      if (current()) setStarting(false);
    }
  }, []);

  const stop = useCallback((onComplete?: (text: string) => void) => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === "inactive") return;
    completeRef.current = onComplete;
    setRecording(false);
    setTranscribing(true);
    recorder.stop();
  }, []);

  const switchMicrophone = useCallback(async (deviceId: string | null) => {
    const input = inputRef.current;
    if (!input) return;
    await input.switchMicrophone(deviceId);
    if (inputRef.current === input) setMicrophoneLabel(input.label);
  }, []);

  return {
    recording,
    starting,
    transcribing,
    microphoneLabel,
    start,
    stop,
    switchMicrophone,
  };
}
